import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { track } from '../lib/analytics';
import { API_BASE } from '../lib/apiBase';

// Responsive hero image pipeline — Phase C
// Desktop hero serves the user's untouched 3200×1450 WebP (no downscale,
// no re-encode). Mobile variants are unchanged; the desktop AVIF files
// are regenerated *only* so mobile DPR=2/3 phones have a sharp fallback
// in the <picture> srcset (they pick `avif_desktop`/`avif_desktop2x`
// when serving high-density phones).
// `?v=4` cache-busts after restoring the mobile fallbacks.
const V = '?v=4';
// Serve hero assets through the `/api/uploads/*` proxy so they get a 1-year
// immutable cache (the bare `/uploads/*` static path is only cached ~5 min).
// The preload <link>s in public/index.html point at the same /api/uploads
// paths so the preload matches the picture's chosen source.
const U = (name) => `${API_BASE}/api/uploads/${name}`;
const HERO = {
  avif_mobile: U('hero_main_mobile.avif'),
  avif_desktop: U('hero_main_desktop.avif') + V,
  avif_desktop2x: U('hero_main_desktop2x.avif') + V,
  webp_mobile: U('hero_main_mobile.webp'),
  webp_desktop: U('hero_main_desktop.webp') + V,
  webp_desktop2x: U('hero_main_desktop2x.webp') + V,
  jpeg_fallback: U('hero_main_desktop.jpg') + V,
};

export function HeroSection() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      track('search_query', { query: q, page: 'hero' });
      navigate(`/Explore?search=${encodeURIComponent(q)}`);
    } else {
      navigate('/Explore');
    }
  };

  return (
    <section className="relative" data-testid="hero-section">
      <div className="relative h-[517px] sm:h-[558px] lg:h-[599px] overflow-hidden">
        <picture>
          {/* Mobile: high-DPR phones need the big variants.
              DPR=1 gets mobile file, DPR=2 gets desktop, DPR=3 gets desktop2x. */}
          <source
            type="image/avif"
            media="(max-width: 767px)"
            srcSet={`${HERO.avif_mobile} 1x, ${HERO.avif_desktop} 2x, ${HERO.avif_desktop2x} 3x`}
          />
          <source
            type="image/webp"
            media="(max-width: 767px)"
            srcSet={`${HERO.webp_mobile} 1x, ${HERO.webp_desktop} 2x, ${HERO.webp_desktop2x} 3x`}
          />
          {/* Desktop: single untouched WebP from source (3200×1450).
              Both DPR=1 and DPR=2 use the same file — zero re-encoding,
              zero downscale. */}
          <source
            type="image/webp"
            media="(min-width: 768px)"
            srcSet={`${HERO.webp_desktop} 1x, ${HERO.webp_desktop2x} 2x`}
          />
          <img
            src={HERO.jpeg_fallback}
            alt="Sitges beach and Mediterranean coastline"
            width="1920"
            height="870"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: 'center top' }}
            fetchPriority="high"
            decoding="async"
          />
        </picture>

        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(13,40,71,0.03) 0%, rgba(13,40,71,0.15) 100%)' }} />

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full max-w-[936px] mx-auto px-0 sm:px-6 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="text-white text-[46px] sm:text-[52px] lg:text-[58px] font-bold leading-[1.1] tracking-tight"
              style={{ fontFamily: "'Outfit', system-ui, sans-serif", textShadow: '0 2px 16px rgba(0,0,0,0.5)' }}
            >
              {t('hero_subtitle')}
            </motion.h1>

            {/* Search Bar */}
            <motion.form
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              onSubmit={handleSearch}
              className="mt-6 sm:mt-7 px-[9px] sm:px-0"
            >
              <div className="relative w-full sm:w-[620px] sm:mx-auto">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={(e) => e.target.select()}
                  placeholder={t('search_experiences')}
                  className="w-full h-[50px] sm:h-[65px] pl-4 sm:pl-7 pr-[90px] sm:pr-[105px] text-[15px] sm:text-[17px] font-bold text-[#1a4d7a] rounded-full
                             bg-white/95 backdrop-blur-sm
                             border border-white/20
                             placeholder:text-[#2d5f8d]/60 placeholder:font-medium
                             focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white
                             transition-all duration-200"
                  style={{ 
                    fontFamily: "'Outfit', sans-serif",
                    boxShadow: '0 4px 24px rgba(0,0,0,0.15), 0 2px 16px rgba(0,0,0,0.5)',
                  }}
                />
                {/* Blue Search Button */}
                <button
                  type="submit"
                  className="absolute right-1 sm:right-[8.5px] top-1/2 -translate-y-1/2 
                             h-[42px] sm:h-[50px] px-4 sm:px-6 rounded-full
                             bg-[#3c84ff] hover:bg-[#2a6fe0]
                             flex items-center justify-center
                             transition-all duration-200
                             text-white font-semibold text-[18px] sm:text-[24px]"
                  aria-label={t('search')}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {t('search')}
                </button>
              </div>
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
}
